import { useCallback, useMemo } from 'react';
import { useSnapshot } from 'valtio';

import {
  usePlayerActions,
  usePlayerActorScene,
  usePlayerResolvedAnimation,
  usePlayerSettings,
  usePlayerStoreApi,
  usePlayerTimeline,
} from '@cartwhl/3d/player';

import { noticesStore, userStore } from 'stores/global';

import { MOTION_EDITING_SUPPORTED_CHARACTER_ID } from '../../constants';
import { requireMotionEditingAccess } from '../../motionEditingAccess';
import { useStore as useMotionEditorStore } from '../../motionEditorStore';
import type { MotionEditorActor } from '../../types';
import {
  formatInterpolatedFrameSnapNotice,
  getSourceBvhForTimeline,
  normalizePlaybackFrame,
  playbackFrameToKeyPoseSourceFrame,
  resolveKeyPosePlaybackFrame,
  sourceFrameToPlaybackFrame,
} from '../keyPoseFrameMapping';
import { reconcileIntervalKeyPoseFrames } from '../keyPoseIntervalAugmentation';
import { resolveSelectedKeyPoseMotionForReference } from '../keyPoseMotionUtils';
import {
  hasKeyPoseBoneSelection,
  hasVisibleUserKeyPoseGhostsForReference,
  isKeyPoseEditableAtPlaybackFrame,
  isKeyPoseFrameEditableForLiveActor,
  selectIntervalKeyPoseRecordsForReference,
  selectKeyPoseRecordsForReference,
  selectKeyPoseTimelineItems,
  selectUserKeyPoseRecordsForReference,
} from '../keyPoseSelectors';
import { captureKeyPoseSnapshotFromSourceScene } from '../keyPoseSnapshots';
import { warnLongSourceMotionEditingIfNeeded } from '../keyPoseSourceMotionLimits';
import {
  useStore as useKeyPoseStore,
  useStoreApi as useKeyPoseStoreApi,
} from '../keyPoseStore';
import {
  createKeyPoseTimingLogger,
  getKeyPoseTimingNow,
} from '../keyPoseTiming';
import { useKeyPoseIntervalAugmentationContext } from './useKeyPoseIntervalAugmentationContext';
import { useKeyPosePersistenceActions } from './useKeyPosePersistenceActions';

export function useKeyPoseFrameCaptureActions() {
  const { isCartwheeler } = useSnapshot(userStore.state);
  const motionEditorStore = useMotionEditorStore();
  const keyPoseStore = useKeyPoseStore();
  const keyPoseStoreApi = useKeyPoseStoreApi();
  const { saveCurrentKeyPoses } = useKeyPosePersistenceActions();
  const playerActions = usePlayerActions();
  const playerStore = usePlayerStoreApi();
  const {
    settings: {
      playback: { frameRate: playbackFrameRate },
    },
  } = usePlayerSettings();
  const { playback, characters, scene } = motionEditorStore.state;
  const keyPoseSnapshot = keyPoseStore.state;
  const activeReferenceName = resolveActiveReferenceName(
    playback.selectedReferenceName,
    playback.sceneActors,
    characters.selectedCharacter?.characterID,
  );
  const keyPoseReferenceName =
    keyPoseSnapshot.activeReferenceName ?? activeReferenceName;
  const actorScene = usePlayerActorScene(activeReferenceName ?? undefined);
  const resolvedAnimation = usePlayerResolvedAnimation(
    activeReferenceName ?? undefined,
  );
  const selectedKeyPoseMotion = activeReferenceName
    ? resolveSelectedKeyPoseMotionForReference(
        activeReferenceName,
        playback.selectedReferenceName,
        playback.selectedSceneMotion,
        playback.sceneActors,
      )
    : null;
  const sourceBvh = getSourceBvhForTimeline(
    resolvedAnimation.bvh,
    resolvedAnimation.bvhs,
    selectedKeyPoseMotion?.timelineIndex,
  );
  const keyPoseCount = selectKeyPoseTimelineItems(
    keyPoseSnapshot,
    keyPoseReferenceName,
  ).length;
  const intervalAugmentation = useKeyPoseIntervalAugmentationContext();

  const shouldDeferNewKeyPoseSaveForUnsupportedCharacter = useCallback(
    (result: {
      created?: boolean;
      keyPoseId: string;
      referenceName: string;
    }) => {
      if (!result.created) {
        return false;
      }

      const hasUnsupportedCharacter = hasUnsupportedMotionEditingCharacter(
        playback.sceneActors,
      );
      if (!hasUnsupportedCharacter) {
        return false;
      }

      keyPoseStoreApi.setUnsupportedCharacterKeyPosePrompt({
        keyPoseId: result.keyPoseId,
        referenceName: result.referenceName,
      });
      return true;
    },
    [keyPoseStoreApi, playback.sceneActors],
  );

  const getMaxFrame = useCallback(() => {
    return Math.max(playerStore.state.transport.duration.frames - 1, 0);
  }, [playerStore]);

  const getCurrentFrame = useCallback(() => {
    return normalizePlaybackFrame(
      playerStore.state.transport.currentFrame,
      getMaxFrame(),
    );
  }, [getMaxFrame, playerStore]);

  const getTimelineKeyPoses = useCallback(
    (maxFrame = getMaxFrame()) =>
      selectKeyPoseTimelineItems(
        keyPoseStoreApi.state,
        keyPoseReferenceName,
      ).map((keyPose) => ({
        ...keyPose,
        frame: sourceFrameToPlaybackFrame({
          bvh: sourceBvh,
          frame: keyPose.frame,
          maxPlaybackFrame: maxFrame,
          playbackFrameRate,
        }),
      })),
    [
      getMaxFrame,
      keyPoseSnapshot.keyPosesById,
      keyPoseSnapshot.referencesByName,
      keyPoseReferenceName,
      keyPoseStoreApi,
      playbackFrameRate,
      sourceBvh,
    ],
  );

  const resolvePlaybackFrameForKeyPose = useCallback(
    (frame: number, maxFrame = getMaxFrame()) => {
      return resolveKeyPosePlaybackFrame({
        bvh: sourceBvh,
        frame,
        maxPlaybackFrame: maxFrame,
        playbackFrameRate,
      });
    },
    [getMaxFrame, playbackFrameRate, sourceBvh],
  );

  const getKeyPoseAtResolvedPlaybackFrame = useCallback(
    (playbackFrame: number, maxFrame = getMaxFrame()) => {
      return (
        getTimelineKeyPoses(maxFrame).find(
          (keyPose) => keyPose.frame === playbackFrame,
        ) ?? null
      );
    },
    [getMaxFrame, getTimelineKeyPoses],
  );

  const captureKeyPoseAtFrame = useCallback(
    (frame: number, actionLabel: string) => {
      const maxFrame = getMaxFrame();
      const requestedPlaybackFrame = normalizePlaybackFrame(frame, maxFrame);
      const playbackFrame = resolvePlaybackFrameForKeyPose(frame, maxFrame);
      if (!activeReferenceName) {
        noticesStore.error(`Select a character before ${actionLabel}.`);
        return null;
      }

      const sourceFrame = playbackFrameToKeyPoseSourceFrame({
        bvh: sourceBvh,
        frame: playbackFrame,
        maxPlaybackFrame: maxFrame,
        playbackFrameRate,
      });
      const capturedKeyPose = captureKeyPoseSnapshotFromSourceScene({
        bvh: sourceBvh,
        frame: sourceFrame,
        scene: actorScene,
      });

      if (!capturedKeyPose) {
        if (!scene.data || !actorScene || !sourceBvh) {
          noticesStore.error('Choose a motion before trying to edit a joint.');
        } else {
          noticesStore.error(
            'Could not capture a key pose. Animation data is not ready.',
          );
        }
        return null;
      }

      return {
        frame: sourceFrame,
        referenceName: activeReferenceName,
        skeletonSnapshot: capturedKeyPose.skeletonSnapshot,
        sourcePose: capturedKeyPose.sourcePose,
        playbackFrame,
        requestedPlaybackFrame,
      };
    },
    [
      activeReferenceName,
      actorScene,
      getMaxFrame,
      playbackFrameRate,
      resolvePlaybackFrameForKeyPose,
      scene.data,
      sourceBvh,
    ],
  );

  const addKeyPoseAtFrame = useCallback(
    (frame: number) => {
      if (!requireMotionEditingAccess(isCartwheeler)) {
        return false;
      }
      if (keyPoseSnapshot.unsupportedCharacterKeyPosePrompt) {
        return false;
      }

      const userKeyPoseCount = selectUserKeyPoseRecordsForReference(
        keyPoseStoreApi.state,
        activeReferenceName,
      ).length;
      if (
        scene.data &&
        activeReferenceName &&
        selectedKeyPoseMotion &&
        userKeyPoseCount === 0
      ) {
        warnLongSourceMotionEditingIfNeeded({
          sceneID: scene.data.sceneID,
          referenceName: activeReferenceName,
          timelineIndex: selectedKeyPoseMotion.timelineIndex,
          sourceBvh,
          userKeyPoseCount,
        });
      }

      const maxFrame = getMaxFrame();
      const capturedKeyPose = captureKeyPoseAtFrame(frame, 'adding a key pose');
      if (!capturedKeyPose) {
        return false;
      }

      const { playbackFrame, requestedPlaybackFrame } = capturedKeyPose;
      const existingKeyPoseAtResolvedFrame = getKeyPoseAtResolvedPlaybackFrame(
        playbackFrame,
        maxFrame,
      );
      if (existingKeyPoseAtResolvedFrame) {
        playerActions.pause();
        playerActions.setFrame(playbackFrame);
        if (existingKeyPoseAtResolvedFrame.hidden) {
          keyPoseStoreApi.showKeyPose(existingKeyPoseAtResolvedFrame.id);
        }
        return true;
      }

      keyPoseStoreApi.clearHoverPreview();
      const result = keyPoseStoreApi.addKeyPose(capturedKeyPose);
      if (result?.didWrite) {
        playerActions.pause();
        playerActions.setFrame(playbackFrame);
        if (requestedPlaybackFrame !== playbackFrame) {
          noticesStore.info(
            formatInterpolatedFrameSnapNotice(
              requestedPlaybackFrame,
              playbackFrame,
            ),
          );
        }
        if (shouldDeferNewKeyPoseSaveForUnsupportedCharacter(result)) {
          return true;
        }

        void saveCurrentKeyPoses('add-key-pose', {
          referenceName: result.referenceName,
        });
        return true;
      }

      if (result) {
        playerActions.pause();
        playerActions.setFrame(playbackFrame);
        return (
          getKeyPoseAtResolvedPlaybackFrame(playbackFrame, maxFrame) !== null
        );
      }

      return false;
    },
    [
      activeReferenceName,
      captureKeyPoseAtFrame,
      getKeyPoseAtResolvedPlaybackFrame,
      getMaxFrame,
      isCartwheeler,
      keyPoseSnapshot.unsupportedCharacterKeyPosePrompt,
      keyPoseStoreApi,
      playerActions,
      saveCurrentKeyPoses,
      scene.data,
      selectedKeyPoseMotion,
      shouldDeferNewKeyPoseSaveForUnsupportedCharacter,
      sourceBvh,
    ],
  );

  const addKeyPoseAtCurrentFrame = useCallback(() => {
    return addKeyPoseAtFrame(getCurrentFrame());
  }, [addKeyPoseAtFrame, getCurrentFrame]);

  const ensureKeyPoseAtFrame = useCallback(
    (frame: number) => {
      if (!requireMotionEditingAccess(isCartwheeler)) {
        return false;
      }

      const maxFrame = getMaxFrame();
      const playbackFrame = resolvePlaybackFrameForKeyPose(frame, maxFrame);
      const keyPoseAtFrame = getKeyPoseAtResolvedPlaybackFrame(
        playbackFrame,
        maxFrame,
      );
      if (keyPoseAtFrame) {
        playerActions.pause();
        playerActions.setFrame(playbackFrame);
        return keyPoseAtFrame.hidden
          ? keyPoseStoreApi.showKeyPose(keyPoseAtFrame.id)
          : true;
      }

      return addKeyPoseAtFrame(frame);
    },
    [
      addKeyPoseAtFrame,
      getKeyPoseAtResolvedPlaybackFrame,
      getMaxFrame,
      isCartwheeler,
      keyPoseStoreApi,
      playerActions,
      resolvePlaybackFrameForKeyPose,
    ],
  );

  const ensureKeyPoseAtCurrentFrame = useCallback(() => {
    return ensureKeyPoseAtFrame(getCurrentFrame());
  }, [ensureKeyPoseAtFrame, getCurrentFrame]);

  const ensureEditableKeyPoseAtFrame = useCallback(
    (frame: number) => {
      if (!requireMotionEditingAccess(isCartwheeler)) {
        return null;
      }
      if (keyPoseSnapshot.unsupportedCharacterKeyPosePrompt) {
        return null;
      }

      const maxFrame = getMaxFrame();
      const playbackFrame = resolvePlaybackFrameForKeyPose(frame, maxFrame);
      const keyPoseAtFrame = getKeyPoseAtResolvedPlaybackFrame(
        playbackFrame,
        maxFrame,
      );
      if (keyPoseAtFrame) {
        const referenceName = keyPoseReferenceName ?? activeReferenceName;
        if (!referenceName) {
          return null;
        }

        playerActions.pause();
        playerActions.setFrame(playbackFrame);
        if (keyPoseAtFrame.hidden) {
          keyPoseStoreApi.showKeyPose(keyPoseAtFrame.id);
        }
        return {
          keyPoseId: keyPoseAtFrame.id,
          created: false,
          didWrite: false,
          frame: keyPoseAtFrame.frame,
          referenceName,
        };
      }

      const capturedKeyPose = captureKeyPoseAtFrame(frame, 'editing a joint');
      if (!capturedKeyPose) {
        return null;
      }

      const { playbackFrame: resolvedPlaybackFrame, requestedPlaybackFrame } =
        capturedKeyPose;
      keyPoseStoreApi.clearHoverPreview();
      const result = keyPoseStoreApi.addKeyPose(capturedKeyPose);
      if (!result) {
        return null;
      }

      playerActions.pause();
      playerActions.setFrame(resolvedPlaybackFrame);
      if (requestedPlaybackFrame !== resolvedPlaybackFrame) {
        noticesStore.info(
          formatInterpolatedFrameSnapNotice(
            requestedPlaybackFrame,
            resolvedPlaybackFrame,
          ),
        );
      }

      if (
        result.didWrite &&
        shouldDeferNewKeyPoseSaveForUnsupportedCharacter(result)
      ) {
        return null;
      }

      return result;
    },
    [
      activeReferenceName,
      captureKeyPoseAtFrame,
      getKeyPoseAtResolvedPlaybackFrame,
      getMaxFrame,
      isCartwheeler,
      keyPoseReferenceName,
      keyPoseSnapshot.unsupportedCharacterKeyPosePrompt,
      keyPoseStoreApi,
      playerActions,
      resolvePlaybackFrameForKeyPose,
      shouldDeferNewKeyPoseSaveForUnsupportedCharacter,
    ],
  );

  const ensureEditableKeyPoseAtCurrentFrame = useCallback(() => {
    return ensureEditableKeyPoseAtFrame(getCurrentFrame());
  }, [ensureEditableKeyPoseAtFrame, getCurrentFrame]);

  const getEditableKeyPoseIdAtCurrentFrame = useCallback(() => {
    if (playerStore.state.transport.isPlaying || !actorScene) {
      return null;
    }

    const currentFrame = getCurrentFrame();
    return (
      getTimelineKeyPoses().find(
        (keyPose) => !keyPose.hidden && keyPose.frame === currentFrame,
      )?.id ?? null
    );
  }, [actorScene, getCurrentFrame, getTimelineKeyPoses, playerStore]);

  const copySourceFrame = useCallback(
    (frame: number) => {
      const capturedKeyPose = captureKeyPoseAtFrame(
        frame,
        'copying a key pose',
      );
      if (!capturedKeyPose) {
        return;
      }

      keyPoseStoreApi.clearHoverPreview();
      keyPoseStoreApi.copyKeyPoseSnapshot(capturedKeyPose);
    },
    [captureKeyPoseAtFrame, keyPoseStoreApi],
  );

  const copyKeyPose = useCallback(
    (keyPoseId: string) => {
      keyPoseStoreApi.clearHoverPreview();
      if (!keyPoseStoreApi.copyKeyPose(keyPoseId)) {
        noticesStore.error('Could not copy key pose.');
      }
    },
    [keyPoseStoreApi],
  );

  const pasteKeyPoseAtFrame = useCallback(
    (frame: number, keyPoseId?: string) => {
      if (!activeReferenceName) {
        noticesStore.error('Select a character before pasting a key pose.');
        return;
      }
      if (keyPoseSnapshot.unsupportedCharacterKeyPosePrompt) {
        return;
      }

      if (!keyPoseStoreApi.state.clipboard) {
        noticesStore.error('Copy a key pose before pasting.');
        return;
      }

      keyPoseStoreApi.clearHoverPreview();
      const targetFrame =
        (keyPoseId
          ? keyPoseStoreApi.state.keyPosesById[keyPoseId]?.frame
          : null) ??
        playbackFrameToKeyPoseSourceFrame({
          bvh: sourceBvh,
          frame,
          playbackFrameRate,
        });
      const result = keyPoseStoreApi.pasteKeyPose({
        frame: targetFrame,
        referenceName: activeReferenceName,
      });
      if (result?.didWrite) {
        if (shouldDeferNewKeyPoseSaveForUnsupportedCharacter(result)) {
          return;
        }

        void saveCurrentKeyPoses('paste-key-pose', {
          referenceName: result.referenceName,
        });
      }
    },
    [
      activeReferenceName,
      keyPoseSnapshot.unsupportedCharacterKeyPosePrompt,
      keyPoseStoreApi,
      playbackFrameRate,
      saveCurrentKeyPoses,
      shouldDeferNewKeyPoseSaveForUnsupportedCharacter,
      sourceBvh,
    ],
  );

  const removeKeyPose = useCallback(
    (keyPoseId: string) => {
      keyPoseStoreApi.clearHoverPreview();
      const result = keyPoseStoreApi.removeKeyPose(keyPoseId);
      if (result) {
        void saveCurrentKeyPoses('remove-key-pose', {
          referenceName: result.referenceName,
        });
      }
    },
    [keyPoseStoreApi, saveCurrentKeyPoses],
  );

  const removeAllKeyPoses = useCallback(() => {
    if (!keyPoseReferenceName) {
      noticesStore.error('Select a character before removing key poses.');
      return;
    }

    keyPoseStoreApi.clearHoverPreview();
    const result = keyPoseStoreApi.removeKeyPosesForReference({
      referenceName: keyPoseReferenceName,
    });
    if (result) {
      void saveCurrentKeyPoses('remove-key-poses', {
        referenceName: result.referenceName,
      });
    }
  }, [keyPoseReferenceName, keyPoseStoreApi, saveCurrentKeyPoses]);

  const toggleKeyPoseHidden = useCallback(
    (keyPoseId: string) => {
      keyPoseStoreApi.clearHoverPreview();
      if (!keyPoseStoreApi.toggleKeyPoseHidden(keyPoseId)) {
        noticesStore.error('Could not update key pose visibility.');
      }
    },
    [keyPoseStoreApi],
  );

  const addIntervalKeyPoses = useCallback(() => {
    const timing = createKeyPoseTimingLogger('addIntervalKeyPoses', {
      activeReferenceName,
      hasIntervalAugmentation: Boolean(intervalAugmentation),
    });

    if (!requireMotionEditingAccess(isCartwheeler)) {
      timing.end('skipped; no access');
      return 0;
    }

    if (!intervalAugmentation || !activeReferenceName) {
      noticesStore.error('Choose a motion before adding interval key poses.');
      timing.end('skipped; missing context', {
        hasActiveReferenceName: Boolean(activeReferenceName),
        hasIntervalAugmentation: Boolean(intervalAugmentation),
      });
      return 0;
    }

    const userKeyPoses = selectUserKeyPoseRecordsForReference(
      keyPoseStoreApi.state,
      activeReferenceName,
    );
    const existingIntervalKeyPoses = selectIntervalKeyPoseRecordsForReference(
      keyPoseStoreApi.state,
      activeReferenceName,
    );
    timing.mark('selected key poses', {
      existingIntervalCount: existingIntervalKeyPoses.length,
      userKeyPoseCount: userKeyPoses.length,
    });

    const { framesToAdd, framesToRemove } = reconcileIntervalKeyPoseFrames({
      maxSourceFrame: intervalAugmentation.maxSourceFrame,
      intervalFrames: intervalAugmentation.intervalFrames,
      userKeyPoses: userKeyPoses.map((keyPose) => ({
        frame: keyPose.frame,
        sourcePose: keyPose.sourcePose,
      })),
      bvh: intervalAugmentation.bvh,
      spacingConfig: intervalAugmentation.spacingConfig,
      existingIntervalFrames: existingIntervalKeyPoses.map(
        (keyPose) => keyPose.frame,
      ),
    });
    timing.mark('reconciled interval frames', {
      framesToAdd: framesToAdd.length,
      framesToRemove: framesToRemove.length,
      intervalFrames: intervalAugmentation.intervalFrames,
      maxSourceFrame: intervalAugmentation.maxSourceFrame,
    });

    if (framesToAdd.length === 0 && framesToRemove.length === 0) {
      console.log(
        '[keyPoseIntervalAugmentation] interval key poses are already up to date',
      );
      timing.end('already up to date');
      return 0;
    }

    const maxFrame = getMaxFrame();
    let addedCount = 0;
    let removedCount = 0;
    const playbackFrameStats = createTimingAccumulator();
    const captureStats = createTimingAccumulator();
    const addStoreStats = createTimingAccumulator();
    const removeStoreStats = createTimingAccumulator();

    keyPoseStoreApi.clearHoverPreview();
    timing.mark('cleared hover preview', { maxFrame });

    for (const sourceFrame of framesToRemove) {
      const intervalKeyPose = existingIntervalKeyPoses.find(
        (keyPose) => keyPose.frame === sourceFrame,
      );
      if (!intervalKeyPose) {
        continue;
      }

      const removeStart = getKeyPoseTimingNow();
      const result = keyPoseStoreApi.removeKeyPose(intervalKeyPose.id);
      recordTiming(removeStoreStats, getKeyPoseTimingNow() - removeStart, {
        sourceFrame,
      });
      if (result) {
        console.log(
          `[keyPoseIntervalAugmentation] key pose removed at ${sourceFrame} frame`,
        );
        removedCount += 1;
      }
    }
    timing.mark('removed stale interval key poses', {
      framesToRemove: framesToRemove.length,
      removedCount,
      removeStore: getTimingAccumulatorSummary(removeStoreStats),
    });

    for (const sourceFrame of framesToAdd) {
      const playbackFrameStart = getKeyPoseTimingNow();
      const playbackFrame = sourceFrameToPlaybackFrame({
        bvh: intervalAugmentation.bvh,
        frame: sourceFrame,
        maxPlaybackFrame: maxFrame,
        playbackFrameRate,
      });
      recordTiming(
        playbackFrameStats,
        getKeyPoseTimingNow() - playbackFrameStart,
        {
          sourceFrame,
        },
      );

      const captureStart = getKeyPoseTimingNow();
      const capturedKeyPose = captureKeyPoseAtFrame(
        playbackFrame,
        'adding interval key poses',
      );
      recordTiming(captureStats, getKeyPoseTimingNow() - captureStart, {
        sourceFrame,
      });
      if (!capturedKeyPose) {
        continue;
      }

      const addStoreStart = getKeyPoseTimingNow();
      const result = keyPoseStoreApi.addKeyPose({
        ...capturedKeyPose,
        stamp: 'interval',
      });
      recordTiming(addStoreStats, getKeyPoseTimingNow() - addStoreStart, {
        sourceFrame,
      });
      if (result?.didWrite) {
        console.log(
          `[keyPoseIntervalAugmentation] key pose added at ${sourceFrame} frame`,
        );
        addedCount += 1;
      }
    }
    timing.mark('added interval key poses', {
      addedCount,
      framesToAdd: framesToAdd.length,
      playbackFrame: getTimingAccumulatorSummary(playbackFrameStats),
      capture: getTimingAccumulatorSummary(captureStats),
      addStore: getTimingAccumulatorSummary(addStoreStats),
    });

    if (addedCount > 0 || removedCount > 0) {
      playerActions.pause();
      timing.mark('paused player');
      keyPoseStoreApi.syncControlFrameGhostVisibility();
      timing.mark('synced control-frame ghost visibility');
      void saveCurrentKeyPoses('add-key-pose', {
        referenceName: activeReferenceName,
      });
      timing.mark('queued interval key-pose save');
    }

    const changedCount = addedCount + removedCount;
    timing.end('completed', {
      addedCount,
      changedCount,
      removedCount,
    });

    return changedCount;
  }, [
    activeReferenceName,
    captureKeyPoseAtFrame,
    getMaxFrame,
    intervalAugmentation,
    isCartwheeler,
    keyPoseStoreApi,
    playbackFrameRate,
    playerActions,
    saveCurrentKeyPoses,
  ]);

  const setHoverPreviewFrame = useCallback(
    (frame: number | null) => {
      if (frame === null || !activeReferenceName) {
        keyPoseStoreApi.clearHoverPreview();
        return;
      }

      const maxFrame = getMaxFrame();
      const playbackFrame = normalizePlaybackFrame(frame, maxFrame);
      const hiddenKeyPoseFrame = selectKeyPoseRecordsForReference(
        keyPoseStoreApi.state,
        activeReferenceName,
      ).find(
        (keyPose) =>
          keyPose.hidden &&
          sourceFrameToPlaybackFrame({
            bvh: sourceBvh,
            frame: keyPose.frame,
            maxPlaybackFrame: maxFrame,
            playbackFrameRate,
          }) === playbackFrame,
      )?.frame;

      keyPoseStoreApi.setHoverPreview({
        frame:
          hiddenKeyPoseFrame ??
          playbackFrameToKeyPoseSourceFrame({
            bvh: sourceBvh,
            frame: playbackFrame,
            playbackFrameRate,
          }),
        referenceName: activeReferenceName,
      });
    },
    [
      activeReferenceName,
      getMaxFrame,
      keyPoseStoreApi,
      playbackFrameRate,
      sourceBvh,
    ],
  );

  return {
    addIntervalKeyPoses,
    addKeyPoseAtCurrentFrame,
    addKeyPoseAtFrame,
    actorScene,
    canPasteKeyPose: Boolean(keyPoseSnapshot.clipboard),
    copyKeyPose,
    copySourceFrame,
    ensureEditableKeyPoseAtCurrentFrame,
    ensureEditableKeyPoseAtFrame,
    ensureKeyPoseAtCurrentFrame,
    ensureKeyPoseAtFrame,
    getCurrentFrame,
    getEditableKeyPoseIdAtCurrentFrame,
    getTimelineKeyPoses,
    keyPoseCount,
    keyPoseReferenceName,
    pasteKeyPoseAtFrame,
    removeAllKeyPoses,
    removeKeyPose,
    setHoverPreviewFrame,
    toggleKeyPoseHidden,
  };
}

export function useKeyPoseGhostVisibilityActions() {
  const keyPoseStore = useKeyPoseStore();
  const keyPoseStoreApi = useKeyPoseStoreApi();
  const { getEditableKeyPoseIdAtCurrentFrame, keyPoseReferenceName } =
    useKeyPoseFrameCaptureActions();
  const timelineKeyPoses = selectKeyPoseTimelineItems(
    keyPoseStore.state,
    keyPoseReferenceName,
  );
  const visibleKeyPoseCount = timelineKeyPoses.filter(
    (keyPose) => !keyPose.hidden,
  ).length;
  const hiddenKeyPoseCount = timelineKeyPoses.length - visibleKeyPoseCount;
  const shouldShowHiddenKeyPoses =
    visibleKeyPoseCount === 0 && hiddenKeyPoseCount > 0;
  const isEditingCurrentKeyPose = hasKeyPoseBoneSelection(
    keyPoseStore.state.editing,
  );

  const toggleKeyPoseVisibility = useCallback(() => {
    if (!keyPoseReferenceName) {
      noticesStore.error(
        'Select a character before updating key pose visibility.',
      );
      return;
    }

    keyPoseStoreApi.clearHoverPreview();
    if (shouldShowHiddenKeyPoses) {
      keyPoseStoreApi.showKeyPosesForReference({
        referenceName: keyPoseReferenceName,
      });
      return;
    }

    keyPoseStoreApi.hideKeyPosesForReference({
      referenceName: keyPoseReferenceName,
      excludeKeyPoseId: getEditableKeyPoseIdAtCurrentFrame(),
    });
  }, [
    getEditableKeyPoseIdAtCurrentFrame,
    keyPoseReferenceName,
    keyPoseStoreApi,
    shouldShowHiddenKeyPoses,
  ]);

  return {
    canToggleKeyPoseVisibility: timelineKeyPoses.length > 0,
    isEditingCurrentKeyPose,
    isShowingHiddenKeyPoses: shouldShowHiddenKeyPoses,
    keyPoseCount: timelineKeyPoses.length,
    toggleKeyPoseVisibility,
  };
}

export function useKeyPoseCaptureActions() {
  const keyPoseStore = useKeyPoseStoreApi();
  const keyPoseSnapshot = useSnapshot(keyPoseStore.state);
  const timeline = usePlayerTimeline();
  const {
    addIntervalKeyPoses,
    addKeyPoseAtCurrentFrame,
    addKeyPoseAtFrame,
    actorScene,
    canPasteKeyPose,
    copyKeyPose,
    copySourceFrame,
    ensureKeyPoseAtFrame,
    getTimelineKeyPoses,
    keyPoseReferenceName,
    pasteKeyPoseAtFrame,
    removeAllKeyPoses,
    removeKeyPose,
    setHoverPreviewFrame,
    toggleKeyPoseHidden,
  } = useKeyPoseFrameCaptureActions();
  const maxFrame = Math.max(timeline.duration.frames - 1, 0);
  const currentFrame = normalizePlaybackFrame(timeline.currentFrame, maxFrame);
  const timelineKeyPoses = useMemo(
    () => getTimelineKeyPoses(maxFrame),
    [
      getTimelineKeyPoses,
      keyPoseSnapshot.keyPosesById,
      keyPoseSnapshot.referencesByName,
      maxFrame,
    ],
  );
  const currentFrameKeyPose =
    timelineKeyPoses.find((keyPose) => keyPose.frame === currentFrame) ?? null;
  const hasVisibleUserKeyPoseGhosts = keyPoseReferenceName
    ? hasVisibleUserKeyPoseGhostsForReference(
        keyPoseStore.state,
        keyPoseReferenceName,
      )
    : false;
  const editableKeyPoseId =
    !timeline.isPlaying &&
    actorScene &&
    currentFrameKeyPose &&
    isKeyPoseEditableAtPlaybackFrame(
      currentFrameKeyPose,
      hasVisibleUserKeyPoseGhosts,
    )
      ? currentFrameKeyPose.id
      : null;
  const hideableKeyPoseCount = timelineKeyPoses.filter(
    (keyPose) => !keyPose.hidden && keyPose.id !== editableKeyPoseId,
  ).length;
  const hiddenKeyPoseCount = timelineKeyPoses.filter(
    (keyPose) => keyPose.hidden,
  ).length;
  const shouldShowHiddenKeyPoses =
    hideableKeyPoseCount === 0 && hiddenKeyPoseCount > 0;

  const ensureKeyPoseAtCurrentFrame = useCallback(() => {
    return ensureKeyPoseAtFrame(currentFrame);
  }, [currentFrame, ensureKeyPoseAtFrame]);

  const toggleKeyPoseVisibility = useCallback(() => {
    if (!keyPoseReferenceName) {
      noticesStore.error(
        'Select a character before updating key pose visibility.',
      );
      return;
    }

    keyPoseStore.clearHoverPreview();
    if (shouldShowHiddenKeyPoses) {
      keyPoseStore.showKeyPosesForReference({
        referenceName: keyPoseReferenceName,
      });
      return;
    }

    keyPoseStore.hideKeyPosesForReference({
      referenceName: keyPoseReferenceName,
      excludeKeyPoseId: editableKeyPoseId,
    });
  }, [
    editableKeyPoseId,
    keyPoseReferenceName,
    keyPoseStore,
    shouldShowHiddenKeyPoses,
  ]);

  return {
    addIntervalKeyPoses,
    addKeyPoseAtCurrentFrame,
    addKeyPoseAtFrame,
    canPasteKeyPose,
    canToggleKeyPoseVisibility:
      hideableKeyPoseCount > 0 || hiddenKeyPoseCount > 0,
    copyKeyPose,
    copySourceFrame,
    currentFrame,
    ensureKeyPoseAtFrame,
    ensureKeyPoseAtCurrentFrame,
    isEditingCurrentKeyPose: Boolean(editableKeyPoseId),
    isShowingHiddenKeyPoses: shouldShowHiddenKeyPoses,
    keyPoseCount: timelineKeyPoses.length,
    maxFrame,
    pasteKeyPoseAtFrame,
    removeAllKeyPoses,
    removeKeyPose,
    setHoverPreviewFrame,
    timelineKeyPoses,
    toggleKeyPoseVisibility,
    toggleKeyPoseHidden,
  };
}

function resolveActiveReferenceName(
  selectedReferenceName: string | null,
  sceneActors: readonly { referenceName: string }[],
  selectedCharacterId?: string | null,
) {
  return (
    selectedReferenceName ??
    (sceneActors.length <= 1
      ? (sceneActors[0]?.referenceName ?? null)
      : null) ??
    selectedCharacterId ??
    null
  );
}

function hasUnsupportedMotionEditingCharacter(
  sceneActors: readonly MotionEditorActor[],
) {
  return sceneActors.some(
    (actor) => !isSupportedMotionEditingCharacterId(actor.characterID),
  );
}

function isSupportedMotionEditingCharacterId(characterID: string) {
  return (
    characterID.replace(/_\d+$/, '') === MOTION_EDITING_SUPPORTED_CHARACTER_ID
  );
}

interface TimingAccumulator {
  count: number;
  maxFrame: number | null;
  maxMs: number;
  totalMs: number;
}

function createTimingAccumulator(): TimingAccumulator {
  return {
    count: 0,
    maxFrame: null,
    maxMs: 0,
    totalMs: 0,
  };
}

function recordTiming(
  accumulator: TimingAccumulator,
  durationMs: number,
  { sourceFrame }: { sourceFrame: number },
) {
  accumulator.count += 1;
  accumulator.totalMs += durationMs;
  if (durationMs > accumulator.maxMs) {
    accumulator.maxMs = durationMs;
    accumulator.maxFrame = sourceFrame;
  }
}

function getTimingAccumulatorSummary(accumulator: TimingAccumulator) {
  return {
    count: accumulator.count,
    maxFrame: accumulator.maxFrame,
    maxMs: roundTimingDuration(accumulator.maxMs),
    totalMs: roundTimingDuration(accumulator.totalMs),
  };
}

function roundTimingDuration(durationMs: number) {
  return Math.round(durationMs * 100) / 100;
}
